import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ExceptionLog } from "@/features/exception-logs/types/exception-log";
import { format } from "date-fns";
import {
  formatExceptionValue,
  formatExceptionHeading,
  DATE_TIME_FORMAT,
} from "@/features/exception-logs/utils/format-exception-value";

interface ExceptionsSummaryProps {
  exceptionLogs: ExceptionLog[];
}

export function ExceptionsSummary({
  exceptionLogs,
}: ExceptionsSummaryProps) {
  const exceptionCount = exceptionLogs?.length || 0;

  if (exceptionCount === 0) {
    return null;
  }

  return (
    <div className="pt-2 border-t border-white/20">
      <div className="flex justify-between items-center gap-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-white/90">
            Exceptions Granted
          </span>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-white/60 hover:text-white/90 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-sm">
                <div className="space-y-3 py-2">
                  {exceptionLogs.map((log, index) => (
                    <div key={log.id}>
                      {index > 0 && (
                        <div className="border-t my-2 border-white/20" />
                      )}
                      <p className="font-medium text-xs mb-1">
                        {formatExceptionHeading(log)}
                      </p>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div>
                          Old:{" "}
                          {formatExceptionValue(
                            log.old_value,
                            log.exception_type,
                            true
                          )}{" "}
                          → New:{" "}
                          {formatExceptionValue(
                            log.new_value,
                            log.exception_type,
                            false
                          )}
                        </div>
                        {log.reason && (
                          <div className="text-xs opacity-75 italic">
                            {log.reason}
                          </div>
                        )}
                        <div className="text-xs opacity-75">
                          {format(new Date(log.created_at), DATE_TIME_FORMAT)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="text-sm font-semibold text-white/90">
          {exceptionCount}
        </div>
      </div>
    </div>
  );
}

